import React, { useRef, useState } from "react";

import angloAmericanLogo from "../../images/partners/anglo-american-logo-high-res.webp";
import botashLogo from "../../images/partners/botash.webp";
import catLogo from "../../images/partners/cat.webp";
import cetaLogo from "../../images/partners/CETA.webp";
import downloadedPartnerLogo from "../../images/partners/download.webp";
import dwsLogo from "../../images/partners/DWS-logo.webp";
import epirocLogo from "../../images/partners/Epiroc.webp";
import highResolutionPartnerLogo from "../../images/partners/HighRes_Logo.webp";
import latLogo from "../../images/partners/LAT.png";
import lepelleWaterLogo from "../../images/partners/lepelewater.webp";
import mqaLogo from "../../images/partners/MQA.webp";
import nsfLogo from "../../images/partners/NSF.webp";
import nssaLogo from "../../images/partners/NSSA.webp";
import pepfarLogo from "../../images/partners/PEPFAR-Logo-Color-+Tagline+medium.webp";
import qctoLogo from "../../images/partners/QCTO-Logo.webp";
import rbPlatLogo from "../../images/partners/RB+PLAT.webp";
import spoticaLogo from "../../images/partners/spotica.webp";
import umalusiLogo from "../../images/partners/Umalusi.webp";
import vaimoLogo from "../../images/partners/Vaimo.webp";
import woolworthsLogo from "../../images/partners/Woolworths-South-Africa-logo.webp";
import "./PartnersSection.css";

const LOGOS_PER_SLIDE = 10;
const SWIPE_THRESHOLD = 48;

const partners = [
  {
    name: "Anglo American",
    logo: angloAmericanLogo,
  },
  {
    name: "Botash",
    logo: botashLogo,
  },
  {
    name: "CAT",
    logo: catLogo,
  },
  {
    name: "CETA",
    logo: cetaLogo,
  },
  {
    name: "Partner organisation",
    logo: downloadedPartnerLogo,
  },
  {
    name: "DWS",
    logo: dwsLogo,
  },
  {
    name: "Epiroc",
    logo: epirocLogo,
  },
  {
    name: "Partner organisation",
    logo: highResolutionPartnerLogo,
  },
  {
    name: "LAT",
    logo: latLogo,
  },
  {
    name: "Lepelle Water",
    logo: lepelleWaterLogo,
  },
  {
    name: "MQA",
    logo: mqaLogo,
  },
  {
    name: "NSF",
    logo: nsfLogo,
  },
  {
    name: "NSSA",
    logo: nssaLogo,
  },
  {
    name: "PEPFAR",
    logo: pepfarLogo,
  },
  {
    name: "QCTO",
    logo: qctoLogo,
  },
  {
    name: "RBPlat",
    logo: rbPlatLogo,
  },
  {
    name: "Spotica",
    logo: spoticaLogo,
  },
  {
    name: "Umalusi",
    logo: umalusiLogo,
  },
  {
    name: "Vaimo",
    logo: vaimoLogo,
  },
  {
    name: "Woolworths South Africa",
    logo: woolworthsLogo,
  },
];

const partnerSlides = Array.from(
  {
    length: Math.ceil(
      partners.length / LOGOS_PER_SLIDE
    ),
  },
  (_, slideIndex) =>
    partners.slice(
      slideIndex * LOGOS_PER_SLIDE,
      (slideIndex + 1) * LOGOS_PER_SLIDE
    )
);

function ArrowIcon({ direction }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "previous" ? (
        <>
          <path d="M19 12H5" />
          <path d="m11 18-6-6 6-6" />
        </>
      ) : (
        <>
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
        </>
      )}
    </svg>
  );
}

function PartnerSlide({
  partners: slidePartners,
  slideIndex,
  activeSlide,
}) {
  return (
    <div
      className="partners-carousel__slide"
      role="group"
      aria-roledescription="slide"
      aria-label={`Partner logos page ${
        slideIndex + 1
      } of ${partnerSlides.length}`}
      aria-hidden={activeSlide !== slideIndex}
    >
      {slidePartners.map(
        (partner, partnerIndex) => (
          <div
            className="partners-carousel__logo-cell"
            key={`${partner.name}-${partnerIndex}`}
          >
            <img
              src={partner.logo}
              alt={partner.name}
              loading="lazy"
              decoding="async"
            />
          </div>
        )
      )}
    </div>
  );
}

export default function PartnersSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(null);

  const goToSlide = (nextSlide) => {
    const totalSlides = partnerSlides.length;

    setActiveSlide(
      (nextSlide + totalSlides) % totalSlides
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToSlide(activeSlide - 1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToSlide(activeSlide + 1);
    }
  };

  const handleTouchStart = (event) => {
    touchStartX.current =
      event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) {
      return;
    }

    const distance =
      event.changedTouches[0].clientX -
      touchStartX.current;

    touchStartX.current = null;

    if (
      Math.abs(distance) < SWIPE_THRESHOLD
    ) {
      return;
    }

    goToSlide(
      distance > 0
        ? activeSlide - 1
        : activeSlide + 1
    );
  };

  return (
    <section
      id="partners"
      className="partners-section"
      aria-labelledby="partners-title"
    >
      <div className="partners-section__inner">
        <header className="partners-section__header">
          <p className="partners-section__eyebrow">
            Our Partners
          </p>

          <h2 id="partners-title">
            Trusted by organisations doing real work.
          </h2>

          <p className="partners-section__intro">
            We work alongside ambitious teams across
            industry, education and enterprise to build
            systems that improve performance.
          </p>
        </header>

        <div
          className="partners-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label="andUs clients, collaborators and delivery partners"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={() => {
            touchStartX.current = null;
          }}
        >
          <div
            className="partners-carousel__track"
            style={{
              transform: `translateX(-${
                activeSlide * 100
              }%)`,
            }}
          >
            {partnerSlides.map(
              (
                slidePartners,
                slideIndex
              ) => (
                <PartnerSlide
                  key={slideIndex}
                  partners={slidePartners}
                  slideIndex={slideIndex}
                  activeSlide={activeSlide}
                />
              )
            )}
          </div>
        </div>

        <footer className="partners-section__footer">
          <p>
            Clients, collaborators and delivery partners.
          </p>

          <div className="partners-carousel__navigation">
            <button
              type="button"
              className="partners-carousel__arrow"
              aria-label="Show previous partner logos"
              onClick={() =>
                goToSlide(activeSlide - 1)
              }
            >
              <ArrowIcon direction="previous" />
            </button>

            <div
              className="partners-carousel__dots"
              aria-label="Choose partner logos page"
            >
              {partnerSlides.map(
                (_, slideIndex) => (
                  <button
                    key={slideIndex}
                    type="button"
                    className={
                      activeSlide === slideIndex
                        ? "is-active"
                        : ""
                    }
                    aria-label={`Show partner logos page ${
                      slideIndex + 1
                    }`}
                    aria-current={
                      activeSlide === slideIndex
                        ? "true"
                        : undefined
                    }
                    onClick={() =>
                      goToSlide(slideIndex)
                    }
                  />
                )
              )}
            </div>

            <button
              type="button"
              className="partners-carousel__arrow"
              aria-label="Show next partner logos"
              onClick={() =>
                goToSlide(activeSlide + 1)
              }
            >
              <ArrowIcon direction="next" />
            </button>
          </div>

          <span
            className="partners-carousel__status"
            aria-live="polite"
          >
            Page {activeSlide + 1} of{" "}
            {partnerSlides.length}
          </span>
        </footer>
      </div>
    </section>
  );
}